import { AgCharts, AgChartProps } from "ag-charts-react";
import { Box, Flex, Paper, Select, Stack, Title } from "@mantine/core";
import { useMemo, useState } from "react";
import { Checkbox, McInputNew } from "../../../components";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import { ISSUE_REGISTER_NAME_OPTIONS } from "../issuesApp/issueConstants";
import { formatShortDate } from "../admin/selectors";
import { useGetAllProjectsForStatsPageQuery, useGetWeeklyStatsQuery } from "./statsRTK";

export interface IStat {
  logDate: Date;
  count: number;
}

const WeeklyUserChart = () => {
  const [fromDate, setFromDate] = useState<Date>(null);
  const [toDate, setToDate] = useState<Date>(new Date());
  const [projectNo, setProjectNo] = useState<number>(null);
  const [toggleUniqueUsers, setToggleUniqueUsers] = useState<boolean>(true);
  const [appName, setAppName] = useState<string>("");
  const [createdFromTemplate, setCreatedFromTemplate] = useState<string>("");

  const appOptions = useMemo(
    () => [
      { name: "Dashboard", value: MCApplicationsUrlEnum.DASHBOARD },
      { name: "Møter", value: MCApplicationsUrlEnum.MEETING },
      { name: "Rapporter", value: MCApplicationsUrlEnum.REPORT },
      { name: "Register", value: MCApplicationsUrlEnum.ISSUE },
      { name: "Sjekkliste", value: MCApplicationsUrlEnum.CHECKLIST },
    ],
    [],
  );

  const issueTemplateOptions = useMemo(
    () =>
      ISSUE_REGISTER_NAME_OPTIONS.map((template) => ({
        name: template.type,
        value: template.type,
      })),
    [],
  );

  const { data: res } = useGetWeeklyStatsQuery({
    fromDate: fromDate?.toISOString(),
    toDate: toDate?.toISOString(),
    projectNo: projectNo,
    toggleUniqueUsers: toggleUniqueUsers === true ? "YES" : "NO",
    appName: appName,
    issueBoardTemplate: createdFromTemplate,
  });

  const { data: projects } = useGetAllProjectsForStatsPageQuery("");

  const projectSelectData = useMemo(
    () => (projects || []).map((project) => ({ label: project.projectName, value: String(project.projectNo) })),
    [projects],
  );

  const appSelectData = useMemo(
    () => appOptions.map((option) => ({ label: option.name, value: option.value })),
    [appOptions],
  );

  const issueTemplateSelectData = useMemo(
    () => issueTemplateOptions.map((option) => ({ label: option.name, value: option.value })),
    [issueTemplateOptions],
  );

  const options: AgChartProps["options"] = {
    data: res?.map((logItem) => ({
      ...logItem,
      logDate: new Date(logItem.logDate),
    })),
    title: {
      text: toggleUniqueUsers === false ? "Appstarter hver uke" : "Unike brukere hver uke",
    },
    axes: [
      {
        type: "time",
        position: "bottom",
      },
      {
        type: "number",
        position: "left",
        title: {
          text: toggleUniqueUsers === false ? "Antall bruker" : " Antall appstarter ",
        },
      },
    ],
    series: [
      {
        type: "area",
        xKey: "logDate",
        yKey: "count",
        yName: toggleUniqueUsers === false ? "Total appstarter" : "Total unike brukere",
        fill: "orange",
        stroke: "orange",
        tooltip: {
          renderer: ({ datum }) => ({
            title: formatShortDate(datum.logDate),
            content: `${toggleUniqueUsers === false ? "Total appstarter" : "Total unike brukere"}: ${datum.count}`,
          }),
        },
      },
    ],
  };

  return (
    <Box m="1rem">
      <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
        <Stack gap="xs">
          <Flex m="0.5rem">
            <Flex direction="column" mr="1.5rem" style={{ maxWidth: "15rem", width: "100%" }}>
              <Title order={5} className="truncateHeaderText" mb={0}>
                Fra
              </Title>
              <McInputNew
                type="date"
                onChange={(e) => setFromDate(e.target.value !== "" ? new Date(e.target.value) : null)}
                value={fromDate != null ? fromDate.toISOString() : null}
              />
            </Flex>
            <Flex direction="column" mr="1.5rem" style={{ maxWidth: "15rem", width: "100%" }}>
              <Title order={5} className="truncateHeaderText" mb={0}>
                Til
              </Title>
              <McInputNew
                type="date"
                onChange={(e) => setToDate(e.target.value !== "" ? new Date(e.target.value) : new Date())}
                value={toDate != null ? toDate.toISOString() : null}
              />
            </Flex>
            <Flex direction="column" mr="1.5rem" style={{ maxWidth: "15rem", width: "100%" }}>
              <Title order={5} className="truncateHeaderText" mb={0}>
                Prosjekt
              </Title>
              <Select
                clearable
                searchable
                limit={100}
                placeholder="Velg prosjekt"
                nothingFoundMessage="Ingen prosjekter funnet"
                data={projectSelectData}
                value={projectNo != null ? String(projectNo) : null}
                onChange={(value) => setProjectNo(value != null ? Number(value) : null)}
                maxDropdownHeight={220}
              />
            </Flex>
            <Flex direction="column" mr="1.5rem" style={{ maxWidth: "15rem", width: "100%" }}>
              <Title order={5} className="truncateHeaderText" mb={0}>
                App
              </Title>
              <Select
                clearable
                searchable
                data={appSelectData}
                value={appName === "" ? null : appName}
                onChange={(value) => setAppName(value ?? "")}
                maxDropdownHeight={220}
              />
            </Flex>
            {appName === MCApplicationsUrlEnum.ISSUE && (
              <Flex direction="column" mr="1.5rem" style={{ maxWidth: "15rem", width: "100%" }}>
                <Title order={5} className="truncateHeaderText" mb={0}>
                  Registermal
                </Title>
                <Select
                  clearable
                  searchable
                  data={issueTemplateSelectData}
                  value={createdFromTemplate === "" ? null : createdFromTemplate}
                  onChange={(value) => setCreatedFromTemplate(value ?? "")}
                  maxDropdownHeight={220}
                />
              </Flex>
            )}
            <Flex direction="column" mr="1.5rem" style={{ maxWidth: "15rem", width: "100%" }}>
              <h5 className="truncateHeaderText" style={{ marginBottom: "0.25rem" }}>
                Unike brukere
              </h5>
              <Checkbox isChecked={toggleUniqueUsers} setChecked={() => setToggleUniqueUsers(!toggleUniqueUsers)} />
            </Flex>
          </Flex>
          <Title order={4} fw={400}>
            Oversikt appstarter
          </Title>
          <AgCharts options={options} />
        </Stack>
      </Paper>
    </Box>
  );
};

export default WeeklyUserChart;
